import { supabase } from "@/integrations/supabase/client";

export interface PedidoContatoData {
  telefone: string | null;
  nome: string | null;
  urlConversa: string | null;
}

/** Busca contato e url_conversa de um pedido (usado no fetchQuery sob demanda) */
export async function fetchPedidoContato(pedidoId: string): Promise<PedidoContatoData> {
  // 1. Buscar via leads_carteira
  const { data: leadData } = await supabase
    .from('leads_carteira')
    .select('id, contato_real_telefone, contato_real_nome, tabulacao_id')
    .eq('pedido_id', pedidoId)
    .maybeSingle();

  let telefone = leadData?.contato_real_telefone || null;
  let nome = leadData?.contato_real_nome || null;

  // 2. Fallback: dados_lead do pedido
  if (!telefone) {
    const { data: pedidoData } = await supabase
      .from('pedidos')
      .select('cliente_id, dados_lead')
      .eq('id', pedidoId)
      .maybeSingle();

    const dadosLead = pedidoData?.dados_lead as Record<string, any> | null;
    if (dadosLead?.contato_real_telefone) {
      telefone = dadosLead.contato_real_telefone;
      nome = dadosLead.contato_real_nome || nome;
    }

    // 3. Fallback: cliente
    if (!telefone && pedidoData?.cliente_id) {
      const { data: clienteData } = await supabase
        .from('clientes')
        .select('whatsapp, telefone_principal')
        .eq('id', pedidoData.cliente_id)
        .maybeSingle();
      telefone = clienteData?.whatsapp || clienteData?.telefone_principal || null;
    }
  }

  let urlConversa: string | null = null;

  // 4. url_conversa via tabulacao_id
  if (leadData?.tabulacao_id) {
    const { data: tabData } = await supabase
      .from('discadora_historico_tabulacao')
      .select('url_conversa')
      .eq('id', leadData.tabulacao_id)
      .maybeSingle();
    urlConversa = tabData?.url_conversa || null;
  }

  // 5. Fallback: por pedido_id
  if (!urlConversa) {
    const { data: tabData } = await supabase
      .from('discadora_historico_tabulacao')
      .select('url_conversa')
      .eq('pedido_id', pedidoId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    urlConversa = tabData?.url_conversa || null;
  }

  // 6. Fallback: histórico do lead
  if (!urlConversa && leadData) {
    const { data: histData } = await supabase
      .from('leads_carteira_historico')
      .select('dados')
      .eq('lead_id', leadData.id)
      .in('acao', ['APRESENTACAO_ENVIADA', 'PROPOSTA_ENVIADA', 'RETORNO_ENVIADO', 'FECHAMENTO_ENVIADO'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    const dados = histData?.dados as Record<string, any> | null;
    urlConversa = dados?.url_conversa || null;
  }

  // 7. Fallback: buscar url_conversa por CNPJ no lead_data do discadora
  if (!urlConversa) {
    // Obter CNPJ do lead ou do pedido
    let cnpj: string | null = null;
    if (leadData) {
      const { data: leadInfo } = await supabase
        .from('leads_carteira')
        .select('cnpj')
        .eq('id', leadData.id)
        .maybeSingle();
      cnpj = leadInfo?.cnpj || null;
    }
    if (!cnpj) {
      const { data: pedidoInfo } = await supabase
        .from('pedidos')
        .select('cliente_id')
        .eq('id', pedidoId)
        .maybeSingle();
      if (pedidoInfo?.cliente_id) {
        const { data: clienteInfo } = await supabase
          .from('clientes')
          .select('cnpj')
          .eq('id', pedidoInfo.cliente_id)
          .maybeSingle();
        cnpj = clienteInfo?.cnpj || null;
      }
    }
    if (cnpj) {
      const { data: tabByCnpj } = await supabase
        .from('discadora_historico_tabulacao')
        .select('url_conversa')
        .contains('lead_data', { cnpj })
        .not('url_conversa', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      urlConversa = tabByCnpj?.url_conversa || null;
    }
  }

  return { telefone, nome, urlConversa };
}
